import React, { useState } from 'react';
import { Clock, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EventPost, EventMode } from '@feature/event-detail/types';
import { CountdownTimer } from './CountdownTimer';
import { formatEventTime } from '@feature/event-detail/utils/formatEventTime';

interface Props {
  event: EventPost;
  mode: EventMode;
  attendeeCount: number;
  isDescriptionExpanded?: boolean;
}

function CountdownSection({ startsAt }: { startsAt: string }): React.ReactElement {
  return (
    <div style={styles.timerSection}>
      <CountdownTimer startsAt={startsAt} />
    </div>
  );
}

function TimeAndAttendeeSection({
  timeDisplay,
  attendeeCount,
}: {
  timeDisplay: string;
  attendeeCount: number;
}): React.ReactElement {
  return (
    <div style={styles.section}>
      <div style={styles.timeRow}>
        <div style={styles.timeContainer}>
          <Clock size={18} color="#3b82f6" />
          <span style={styles.timeText}>{timeDisplay}</span>
        </div>
        <div style={styles.attendeeContainer}>
          <Users size={18} color="#3b82f6" />
          <span style={styles.timeText}>
            {attendeeCount} joined
          </span>
        </div>
      </div>
    </div>
  );
}

function DescriptionSection({
  messageText,
  isDescriptionExpanded,
  onSeeFullDetails,
}: {
  messageText: string | null | undefined;
  isDescriptionExpanded: boolean;
  onSeeFullDetails: () => void;
}): React.ReactElement {
  const descriptionPreviewStyle: React.CSSProperties = {
    ...styles.descriptionPreview,
    WebkitLineClamp: isDescriptionExpanded ? 'unset' : 3,
  };

  if (messageText) {
    return (
      <div style={styles.section}>
        <div style={styles.sectionLabel}>OVERVIEW</div>
        <div style={descriptionPreviewStyle}>
          {messageText.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index < messageText.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
        <button style={styles.seeDetailsButton} onClick={onSeeFullDetails}>
          <span style={styles.seeDetailsButtonText}>
            See full details
          </span>
        </button>
      </div>
    );
  }

  return <></>;
}

export function EventInfo({ event, mode, attendeeCount, isDescriptionExpanded = false }: Props): React.ReactElement {
  const navigate = useNavigate();
  const timeDisplay = formatEventTime(event.starts_at, event.ends_at);

  const handleSeeFullDetails = () => {
    navigate(`/event/${event.share_id}/details`);
  };

  return (
    <div style={styles.container}>
      {mode === 'before' && <CountdownSection startsAt={event.starts_at} />}

      {(mode === 'during' || mode === 'after') && (
        <TimeAndAttendeeSection timeDisplay={timeDisplay} attendeeCount={attendeeCount} />
      )}

      <DescriptionSection
        messageText={event.message_text}
        isDescriptionExpanded={isDescriptionExpanded}
        onSeeFullDetails={handleSeeFullDetails}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    paddingTop: '24px',
    paddingBottom: 0,
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  timerSection: {
    marginBottom: '12px',
    marginTop: 0,
  },
  section: {
    marginBottom: '6px',
  },
  timeRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  timeContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  attendeeContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  timeText: {
    color: '#666',
    fontSize: '14px',
  },
  sectionLabel: {
    color: '#999',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    marginBottom: '8px',
    marginTop: '8px',
    textTransform: 'uppercase',
  },
  descriptionPreview: {
    color: '#000',
    fontSize: '14px',
    lineHeight: '32px',
    marginBottom: '12px',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
  },
  seeDetailsButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'block',
    margin: '0 auto',
  },
  seeDetailsButtonText: {
    color: '#3b82f6',
    fontSize: '13px',
    fontWeight: 600,
  },
};
